import { DefaultQueryParams, PaginatedResponse } from '@frontend/api-utils';
import { Dimension, Location } from '@frontend/common';

import { SlotRendering } from './slot-rendering';

export interface ModuleRendering {
    id: string;
    account_id: string;
    spot_id: string;
    index: string;
    name: string | null;
    type: string | null;
    location: Location | null;
    absolute: Location | null;
    dimension: Dimension | null;
    doors: number | null;
    occupied: boolean;
    /** When false: platform/admin omit from layout; kiosk SLOT grid (virtual SPOTs). Missing/true = render. */
    renderable?: boolean;
    slots: SlotRendering[];
}

/** Spot layout: treat missing/true as renderable. */
export const isModuleRenderingRenderable = (module: Pick<ModuleRendering, 'renderable'>): boolean => module.renderable !== false;

/** How the SPOT layout should draw a module. */
export type ModuleSpotLayoutMode = 'full' | 'hidden' | 'slots-only' | 'slot-list';

/** Fallback when `renderable === false` (not the expanded-renderable `slots-only` path). */
export type ModuleWhenNotRenderable = 'hidden' | 'slot-list';

/**
 * Platform/admin use `hidden` when not renderable; kiosk uses `slot-list`.
 * Non-renderable takes precedence over expand so the module box is never drawn.
 * Expanded renderable modules still resolve to `slots-only` (geometric slots, no chrome).
 */
export const getModuleSpotLayoutMode = (
    module: Pick<ModuleRendering, 'renderable'>,
    options: { isExpanded?: boolean; whenNotRenderable: ModuleWhenNotRenderable }
): ModuleSpotLayoutMode => {
    if (!isModuleRenderingRenderable(module)) {
        return options.whenNotRenderable;
    }
    if (options.isExpanded) {
        return 'slots-only';
    }
    return 'full';
};

/**
 * Prefer module-api `renderable` when it is an explicit boolean.
 * Do not overwrite with `undefined` — that would wipe a false from spot rendering.
 */
export const applyModuleRenderableFlags = <T extends Pick<ModuleRendering, 'id' | 'renderable'>>(
    modules: T[],
    renderableById: ReadonlyMap<string, boolean | undefined>
): T[] =>
    modules.map((module) => {
        if (!renderableById.has(module.id)) {
            return module;
        }
        const flag = renderableById.get(module.id);
        if (typeof flag !== 'boolean') {
            return module;
        }
        return { ...module, renderable: flag };
    });

export type ModuleRenderingListResponse = PaginatedResponse<ModuleRendering>;
export type ModuleRenderingQueryParams = DefaultQueryParams;
